import { useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/design-system/primitives";
import { useArticle, useGuide } from "@/lib/api/hooks";
import type { LearningResource } from "@/types/api";
import { ArticleBody } from "@/features/learn/ArticleDetail";
import { withGuideMedia } from "@/features/learn/selfInjectionGuide";

export function LearningResourcePreview({
  resource,
}: {
  resource: LearningResource;
}) {
  return (
    <div className="py-20">
      <header className="mb-20 flex flex-col gap-10 px-20">
        <p className="type-label-xs text-ink-secondary">
          {resource.kind === "guide" ? "Step-by-step guide" : "Article"} ·
          Patient preview
        </p>
        <h3 className="text-24 leading-120">{resource.title}</h3>
        {resource.snippet && (
          <p className="text-14 leading-150 text-ink-secondary">
            {resource.snippet}
          </p>
        )}
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-44 items-center gap-8 self-start text-14 underline focus-visible:shadow-focus"
        >
          Open in portal <ExternalLink size={14} aria-hidden />
        </a>
      </header>
      {resource.kind === "guide" ? (
        <GuidePreview slug={resource.slug} />
      ) : (
        <ArticlePreview slug={resource.slug} />
      )}
    </div>
  );
}

function PreviewUnavailable({ retry }: { retry: () => void }) {
  return (
    <div role="alert" className="px-20 text-14">
      <p>
        The preview could not be loaded. Retry or open the resource in the
        portal.
      </p>
      <Button
        type="button"
        variant="ghost"
        className="mt-8 min-h-44"
        onClick={retry}
      >
        Retry preview
      </Button>
    </div>
  );
}

function PreviewLoading() {
  return (
    <div role="status" className="mx-20 h-180 animate-pulse bg-sunken">
      <span className="sr-only">Fetching preview…</span>
    </div>
  );
}

function ArticlePreview({ slug }: { slug: string }) {
  const article = useArticle(slug);
  if (article.isLoading) return <PreviewLoading />;
  if (article.isError || !article.data)
    return <PreviewUnavailable retry={() => void article.refetch()} />;
  return <ArticleBody article={article.data} />;
}

function GuidePreview({ slug }: { slug: string }) {
  const result = useGuide(slug);
  const [index, setIndex] = useState(0);
  if (result.isLoading) return <PreviewLoading />;
  if (result.isError || !result.data)
    return <PreviewUnavailable retry={() => void result.refetch()} />;
  // Use the same published step/media overrides as the patient-facing guide.
  const guide = withGuideMedia(result.data);
  const stepIndex = Math.min(index, Math.max(guide.steps.length - 1, 0));
  const step = guide.steps[stepIndex];
  if (!step)
    return <p className="px-20 text-14">No steps available to preview.</p>;
  return (
    <div className="flex flex-col gap-15 px-20">
      <div className="flex items-center justify-between gap-10 border-y border-line-subtle py-8">
        <span className="type-label-sm">
          Step {stepIndex + 1} of {guide.steps.length}
        </span>
        <div className="flex gap-5">
          <Button
            type="button"
            variant="ghost"
            className="h-44 w-44 p-0"
            aria-label="Previous step"
            disabled={stepIndex === 0}
            onClick={() => setIndex(stepIndex - 1)}
          >
            <ChevronLeft size={18} aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-44 w-44 p-0"
            aria-label="Next step"
            disabled={stepIndex === guide.steps.length - 1}
            onClick={() => setIndex(stepIndex + 1)}
          >
            <ChevronRight size={18} aria-hidden />
          </Button>
        </div>
      </div>
      {step.media_url &&
        (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(step.media_url) ? (
          <video
            key={step.media_url}
            src={step.media_url}
            controls
            playsInline
            preload="metadata"
            className="aspect-video w-full bg-sunken"
            aria-label={step.title}
          />
        ) : (
          <img
            src={step.media_url}
            alt={step.title}
            className="max-h-[30rem] w-full object-contain"
          />
        ))}
      <h4 className="text-20 leading-120">{step.title}</h4>
      <p className="whitespace-pre-line text-16 leading-150">{step.body}</p>
      {step.note && (
        <aside className="border border-line-subtle bg-raised p-15 text-14 leading-150">
          <p>{step.note.title}</p>
          <p>{step.note.body}</p>
          <a
            href={step.note.link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {step.note.link.label}
          </a>
        </aside>
      )}
      {step.audio_url && (
        <audio
          key={step.audio_url}
          src={step.audio_url}
          controls
          preload="none"
          aria-label="Step voiceover"
          className="w-full"
        />
      )}
      {step.transcript && (
        <details className="text-14 leading-150">
          <summary className="min-h-44 cursor-pointer py-10">
            Read transcript
          </summary>
          <p className="whitespace-pre-line text-ink-secondary">
            {step.transcript}
          </p>
        </details>
      )}
    </div>
  );
}
